import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { usePhoneCallService } from '../../services/phoneCallService';
import { useCatalogService } from '../../services/catalogService';
import type { Specialist } from '../../types/phoneCapture';
import {
  deliveryMethodsMatch,
  labelForDeliveryMethodDbCode,
  toDeliveryMethodDbCode,
} from '../../utils/deliveryMethod';

interface ServiceSpecialistAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  serviceType: string;
  serviceTypeDescription?: string;
  /** Método de entrega del servicio (prefiltra la lista). */
  deliveryMethod?: string | null;
  deliveryMethodOptions?: Array<{ code: string; description: string }>;
  selectedSpecialistId?: number | null;
  onAssign: (specialist: Specialist) => void;
}

/** Códigos de modalidad / delivery method asociados al especialista. */
const getSpecialistDeliveryCodes = (specialist: Specialist): string[] => {
  const raw =
    String((specialist as any).deliveryMethodCodes || (specialist as any).deliveryMethods || '').trim();
  const fromCsv = raw
    ? raw.split(',').map((m: string) => m.trim()).filter(Boolean)
    : [];
  const fromModalities = (specialist.modalities || [])
    .map((m) => String(m.code || m.name || '').trim())
    .filter(Boolean);
  return Array.from(new Set([...fromCsv, ...fromModalities].map(toDeliveryMethodDbCode).filter(Boolean)));
};

const specialistSupportsDeliveryMethod = (specialist: Specialist, deliveryMethod: string): boolean => {
  const target = toDeliveryMethodDbCode(deliveryMethod);
  if (!target) return true;
  const codes = getSpecialistDeliveryCodes(specialist);
  if (codes.length === 0) return false;
  return codes.some((code) => deliveryMethodsMatch(code, target));
};

const ServiceSpecialistAssignmentModal: React.FC<ServiceSpecialistAssignmentModalProps> = ({
  open,
  onClose,
  serviceType,
  serviceTypeDescription,
  deliveryMethod = null,
  deliveryMethodOptions = [],
  selectedSpecialistId,
  onAssign
}) => {
  const { getAllSpecialists, getSpecialistById } = usePhoneCallService();
  const { getCoverageAreas, getAllCatalogs } = useCatalogService();
  const [allSpecialists, setAllSpecialists] = useState<Specialist[]>([]);
  const [coverageAreas, setCoverageAreas] = useState<any[]>([]);
  const [catalogDeliveryMethods, setCatalogDeliveryMethods] = useState<
    Array<{ code: string; description: string }>
  >([]);
  const [coverageAreaFilter, setCoverageAreaFilter] = useState<string>('ALL');
  const [deliveryMethodFilter, setDeliveryMethodFilter] = useState<string>('ALL');
  const [nameSearch, setNameSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [loadError, setLoadError] = useState<string>('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [specialistDetail, setSpecialistDetail] = useState<Specialist | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const resolvedDeliveryOptions =
    deliveryMethodOptions.length > 0 ? deliveryMethodOptions : catalogDeliveryMethods;

  useEffect(() => {
    if (!open) return;
    const loadData = async () => {
      try {
        setLoading(true);
        setCoverageLoading(true);
        setLoadError('');
        const needsCatalog = deliveryMethodOptions.length === 0;
        const [specialistsRes, coverageAreasRes, catalogsRes] = await Promise.all([
          getAllSpecialists(0, 100, 'specialistId', 'asc', undefined),
          getCoverageAreas(),
          needsCatalog ? getAllCatalogs() : Promise.resolve(null)
        ]);
        setAllSpecialists(specialistsRes?.specialists || []);
        setCoverageAreas(coverageAreasRes || []);
        if (catalogsRes?.DELIVERY_METHOD) {
          setCatalogDeliveryMethods(catalogsRes.DELIVERY_METHOD || []);
        }
      } catch (error) {
        console.error('Error loading assignment modal data:', error);
        setAllSpecialists([]);
        setCoverageAreas([]);
        setLoadError('No se pudieron cargar especialistas o áreas de cobertura.');
      } finally {
        setLoading(false);
        setCoverageLoading(false);
      }
    };

    const prefilterRaw = (deliveryMethod || '').trim();
    if (!prefilterRaw) {
      setDeliveryMethodFilter('ALL');
    } else {
      const options =
        deliveryMethodOptions.length > 0 ? deliveryMethodOptions : catalogDeliveryMethods;
      const matched = options.find((item) =>
        deliveryMethodsMatch(item.code, prefilterRaw)
      );
      setDeliveryMethodFilter(matched?.code || prefilterRaw);
    }
    setCoverageAreaFilter('ALL');
    setNameSearch('');
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deliveryMethod]);

  const filteredSpecialists = useMemo(() => {
    let filtered = allSpecialists.filter((specialist) => specialist.productArea === serviceType);

    if (coverageAreaFilter !== 'ALL') {
      filtered = filtered.filter(
        (specialist) => specialist.coverageArea?.coverageAreaId?.toString() === coverageAreaFilter
      );
    }

    if (deliveryMethodFilter !== 'ALL') {
      filtered = filtered.filter((specialist) =>
        specialistSupportsDeliveryMethod(specialist, deliveryMethodFilter)
      );
    }

    const q = nameSearch.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((specialist) =>
        (specialist.fullName || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [allSpecialists, serviceType, coverageAreaFilter, deliveryMethodFilter, nameSearch]);

  const handleOpenSpecialistDetail = async (specialistId: number) => {
    try {
      setDetailLoading(true);
      const fullSpecialist = await getSpecialistById(specialistId);
      setSpecialistDetail(fullSpecialist || null);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error loading specialist detail:', error);
      setLoadError('No se pudo cargar el detalle del especialista.');
    } finally {
      setDetailLoading(false);
    }
  };

  const getDeliveryMethodLabel = (method: string) => {
    const fromCatalog = resolvedDeliveryOptions.find((item) =>
      deliveryMethodsMatch(item.code, method)
    );
    if (fromCatalog?.description) return fromCatalog.description;
    return labelForDeliveryMethodDbCode(method);
  };

  const deliveryFilterDescription =
    deliveryMethodFilter !== 'ALL' ? getDeliveryMethodLabel(deliveryMethodFilter) : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Canalización del Servicio</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {loadError && <Alert severity="error">{loadError}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Servicio seleccionado: <strong>{serviceTypeDescription || serviceType || 'N/D'}</strong>
            {deliveryFilterDescription ? (
              <>
                {' '}
                · Método de entrega: <strong>{deliveryFilterDescription}</strong>
              </>
            ) : null}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar por nombre"
                placeholder="Nombre del especialista"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel>Método de entrega</InputLabel>
                <Select
                  label="Método de entrega"
                  value={deliveryMethodFilter}
                  onChange={(e) => setDeliveryMethodFilter(e.target.value)}
                >
                  <MenuItem value="ALL">Todos</MenuItem>
                  {resolvedDeliveryOptions.map((item) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={coverageLoading}>
                <InputLabel>Área de cobertura</InputLabel>
                <Select
                  label="Área de cobertura"
                  value={coverageAreaFilter}
                  onChange={(e) => setCoverageAreaFilter(e.target.value)}
                >
                  <MenuItem value="ALL">Todas</MenuItem>
                  {coverageAreas.map((area: any) => (
                    <MenuItem key={area.coverageAreaId} value={area.coverageAreaId.toString()}>
                      {area.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {loading ? (
            <Typography variant="body2">Cargando especialistas...</Typography>
          ) : filteredSpecialists.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay especialistas disponibles para este servicio con los filtros seleccionados.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {filteredSpecialists.map((specialist) => {
                const isSelected =
                  selectedSpecialistId != null && selectedSpecialistId === specialist.specialistId;
                const modalities = getSpecialistDeliveryCodes(specialist);

                return (
                  <Paper key={specialist.specialistId} variant="outlined" sx={{ p: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {specialist.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Área: {specialist.coverageArea?.code || 'Sin área'} | Servicio:{' '}
                          {specialist.productArea}
                          {modalities.length > 0
                            ? ` | Entrega: ${modalities.map(getDeliveryMethodLabel).join(', ')}`
                            : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                        <Tooltip title={isSelected ? 'Especialista asignado' : 'Asignar especialista'}>
                          <span>
                            <IconButton
                              color={isSelected ? 'success' : 'primary'}
                              onClick={() => onAssign(specialist)}
                            >
                              {isSelected ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Ver detalle">
                          <span>
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenSpecialistDetail(specialist.specialistId)}
                              disabled={detailLoading}
                            >
                              <VisibilityOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
      </Dialog>

      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalle del especialista</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography variant="body2">Cargando detalle...</Typography>
          ) : specialistDetail ? (
            <Stack spacing={2}>
              <Typography variant="h6">{specialistDetail.fullName}</Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(specialistDetail as any).coverageArea && (
                  <Chip label={`Área: ${(specialistDetail as any).coverageArea.code}`} variant="outlined" />
                )}
                {specialistDetail.productArea && (
                  <Chip label={`Servicio: ${specialistDetail.productArea}`} variant="outlined" />
                )}
                <Chip
                  label={(specialistDetail as any).active ? 'Activo' : 'Inactivo'}
                  color={(specialistDetail as any).active ? 'success' : 'error'}
                  variant="outlined"
                />
              </Stack>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Especialidades
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(specialistDetail.specialties || []).length > 0 ? (
                    (specialistDetail.specialties || []).map((specialty) => (
                      <Chip
                        key={specialty.specialtyId}
                        label={(specialty as any).name || (specialty as any).description || 'N/D'}
                        size="small"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin especialidades registradas.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Modalidades de atención
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {((specialistDetail as any).deliveryMethodCodes || '')
                    .split(',')
                    .map((method: string) => method.trim())
                    .filter((method: string) => method.length > 0).length > 0 ? (
                    ((specialistDetail as any).deliveryMethodCodes || '')
                      .split(',')
                      .map((method: string) => method.trim())
                      .filter((method: string) => method.length > 0)
                      .map((method: string, index: number) => (
                        <Chip
                          key={`${method}-${index}`}
                          label={getDeliveryMethodLabel(method) || method}
                          size="small"
                          variant="outlined"
                        />
                      ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin modalidades registradas.
                    </Typography>
                  )}
                </Stack>
              </Box>

              {(specialistDetail as any).contacts && (specialistDetail as any).contacts.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Contactos
                  </Typography>
                  <Stack spacing={1}>
                    {(specialistDetail as any).contacts.map((contact: any, index: number) => (
                      <Paper
                        key={`${contact.contactType || contact.type || 'contact'}-${index}`}
                        variant="outlined"
                        sx={{ p: 1 }}
                      >
                        <Typography variant="body2">
                          {contact.contactType || contact.type || 'Tipo'}:{' '}
                          {contact.contactValue || contact.value || 'N/D'}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}

              {(specialistDetail as any).temporaryAddresses &&
                Array.isArray((specialistDetail as any).temporaryAddresses) &&
                (specialistDetail as any).temporaryAddresses.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Direcciones de atención
                    </Typography>
                    <Stack spacing={1}>
                      {(specialistDetail as any).temporaryAddresses.map((tempAddr: any, index: number) => (
                        <Paper
                          key={`${tempAddr.id || tempAddr.zipCode || 'temp'}-${index}`}
                          variant="outlined"
                          sx={{ p: 1 }}
                        >
                          <Typography variant="body2">
                            {tempAddr.direccion || 'Sin dirección registrada'}
                          </Typography>
                          {(tempAddr.horario || tempAddr.notas) && (
                            <Typography variant="caption" color="text.secondary">
                              {tempAddr.horario ? `Horario: ${tempAddr.horario}` : ''}
                              {tempAddr.horario && tempAddr.notas ? ' | ' : ''}
                              {tempAddr.notas ? `Notas: ${tempAddr.notas}` : ''}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No hay información de detalle para mostrar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceSpecialistAssignmentModal;
